import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { formatMoney } from "@/lib/format";
import { orderStatusLabel } from "@/lib/orderStatus";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  NEW: "bg-blue-100 text-blue-800",
  UNDER_REVIEW: "bg-blue-100 text-blue-800",
  VERIFIED: "bg-emerald-100 text-emerald-800",
  PAYMENT_CONFIRMED: "bg-emerald-100 text-emerald-800",
  READY_FOR_DELIVERY: "bg-indigo-100 text-indigo-800",
  OUT_FOR_DELIVERY: "bg-purple-100 text-purple-800",
  DELIVERED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  CANCELLED: "bg-slate-100 text-slate-800",
};

const STEP_ORDER = ["NEW", "VERIFIED", "READY_FOR_DELIVERY", "OUT_FOR_DELIVERY", "DELIVERED"];
const STEP_LABEL = {
  NEW: "Pending",
  VERIFIED: "Confirmed",
  READY_FOR_DELIVERY: "Ready",
  OUT_FOR_DELIVERY: "On the way",
  DELIVERED: "Delivered",
};
const STATUS_TO_STEP = {
  NEW: 0,
  UNDER_REVIEW: 0,
  VERIFIED: 1,
  PAYMENT_CONFIRMED: 1,
  READY_FOR_DELIVERY: 2,
  OUT_FOR_DELIVERY: 3,
  DELIVERED: 4,
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order_number") || "");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const track = async (e) => {
    if (e) e.preventDefault();
    setBusy(true); setError(""); setData(null);
    try {
      const { data } = await axios.post(`${API_URL}/orders/track`, {
        order_number: orderNumber.trim(),
        phone: phone.trim(),
      });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error?.message || "Order not found. Check your order number and phone.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (params.get("order_number")) setOrderNumber(params.get("order_number"));
  }, [params]);

  return (
    <div className="min-h-screen bg-background" data-testid="track-order-root">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Button
            asChild
            size="sm"
            variant="ghost"
            className="-ml-2"
            data-testid="track-back-btn"
          >
            <Link to="/">← Back</Link>
          </Button>
          <Link to="/" className="font-semibold text-lg">FAST FORCE</Link>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Track order</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Look up your order</CardTitle>
            <CardDescription>Enter your order number and the phone you placed it with.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={track} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="track-order-form">
              <Input
                required
                placeholder="ORD-2026-000001"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                data-testid="track-order-number"
              />
              <Input
                required
                placeholder="+971 5x xxx xxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="track-order-phone"
              />
              <Button type="submit" disabled={busy} data-testid="track-order-submit">
                {busy ? "Looking up…" : "Track"}
              </Button>
            </form>
            {error && <Alert variant="destructive" className="mt-4" data-testid="track-order-error"><AlertDescription>{error}</AlertDescription></Alert>}
          </CardContent>
        </Card>

        {data && (
          <Card data-testid="track-order-result">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-mono">{data.order_number}</CardTitle>
                <CardDescription>{data.customer_name}</CardDescription>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${STATUS_TONE[data.status] || "bg-slate-100"}`}>
                {orderStatusLabel(data.status)}
              </span>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* progress bar */}
              {!["REJECTED", "CANCELLED"].includes(data.status) && (
                <div className="grid grid-cols-5 gap-1 mt-2" data-testid="track-order-progress">
                  {STEP_ORDER.map((step, i) => {
                    const currentIdx = STATUS_TO_STEP[data.status] ?? -1;
                    const done = currentIdx >= i;
                    return (
                      <div key={step} className="text-center">
                        <div className={`h-2 rounded ${done ? "bg-emerald-600" : "bg-slate-200"}`} />
                        <div className={`text-[10px] mt-1 ${done ? "text-emerald-700 font-medium" : "text-muted-foreground"}`}>
                          {STEP_LABEL[step] || step.replace(/_/g, " ")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Delivery to</div>
                  <div>{data.delivery_address?.area}, {data.delivery_address?.emirate}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total</div>
                  <div className="font-medium">
                    {formatMoney({ amount_minor: data.totals?.grand_total_minor || 0, currency: data.totals?.currency || "AED" })}
                    <span className="text-xs text-muted-foreground ml-2">· {data.items_count} items</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Payment</div>
                  <div>
                    {(data.payment?.method || "—").replace(/_/g, " ")}
                    <Badge variant="secondary" className="ml-2 text-[10px]">{data.payment?.status}</Badge>
                  </div>
                </div>
                {data.delivery && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Delivery</div>
                    <div>
                      {data.delivery.driver_name ? `${data.delivery.driver_name} · ` : ""}{data.delivery.status || "not assigned"}
                    </div>
                  </div>
                )}
              </div>

              {data.rejected_reason && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <div className="font-semibold mb-1">Order not processed</div>
                    <div className="text-xs">{data.rejected_reason}</div>
                  </AlertDescription>
                </Alert>
              )}

              {data.invoice && (
                <div className="rounded border p-3 bg-muted/40 text-sm">
                  <div className="font-medium">Tax invoice available</div>
                  <div className="text-xs text-muted-foreground font-mono">{data.invoice.invoice_number}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Contact us to receive a copy — invoices are emailed once your order is completed.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
